import React, { useState, useEffect } from "react";
import styled, { css } from "styled-components";
import Sortable from "../../Sortable";
import arrayMove from "array-move";
import Modal from "../../Modal";
import Input from "../../Input";
import Button from "../../Button";
import { ReactComponent as Delete } from "../../../assets/svg/delete.svg";
import { ReactComponent as Edit } from "../../../assets/svg/edit.svg";
import { ReactComponent as Add } from "../../../assets/svg/add.svg";
import { ReactComponent as Search } from "../../../assets/svg/search3.svg";
import { v4 as uuid } from "uuid";
import clean from "clean-deep";

const Tree = ({ seed = {}, onChange = () => {}, onItemClick = () => {} }) => {
  const [state, setState] = useState([]);
  const [title, setTitle] = useState("");
  const [showState, setShowState] = useState(undefined);

  useEffect(() => {
    setState(
      Object.keys(clean(seed || {}))
        .map((key) => ({ id: key, ...seed[key] }))
        .sort((a, b) => a.order - b.order)
    );
  }, [seed]);

  useEffect(() => {
    onChange({
      data: state.reduce(
        (prev, curr, index) => ({
          ...prev,
          [curr.id]: {
            ...curr,
            order: index,
          },
        }),
        {}
      ),
    });
  }, [state]);

  const onSortEnd = ({ oldIndex, newIndex }) =>
    setState((state) => arrayMove(state, oldIndex, newIndex));

  return (
    <Container>
      {showState && (
        <ModalStyled>
          <ModalContainer>
            <ModalTitle>Título</ModalTitle>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} />
            <Buttons>
              <ButtonStyled
                text="Cancelar"
                secondary
                onClick={() => {
                  setShowState(undefined);
                  setTitle("");
                }}
              />
              {showState?.state === "EDIT" && (
                <ButtonStyled
                  text="Guardar"
                  primary
                  onClick={() => {
                    setState((state) => {
                      const temp = [...state];
                      temp[showState?.index] = {
                        ...temp[showState?.index],
                        title: title,
                      };
                      return temp;
                    });
                    setShowState(undefined);
                  }}
                />
              )}
              {showState?.state === "ADD" && (
                <ButtonStyled
                  text="Agregar"
                  primary
                  onClick={() => {
                    setState((state) => {
                      const temp = [...state];
                      temp.push({ id: uuid(), title });
                      return temp;
                    });
                    setShowState(undefined);
                  }}
                />
              )}
            </Buttons>
          </ModalContainer>
        </ModalStyled>
      )}
      <TreeOptions>
        <OptionIcon
          as={Add}
          onClick={() => {
            setShowState({ state: "ADD" });
            setTitle("");
          }}
        />
      </TreeOptions>
      <Sortable
        items={state}
        render={(item, index) => (
          <Row key={index} published={item.published} deleted={item.deleted}>
            <Text>{item.title}</Text>
            <Options>
              <OptionIcon
                as={Delete}
                onClick={() =>
                  setState((state) => {
                    const temp = [...state];
                    if (item.published)
                      temp[index] = {
                        ...temp[index],
                        deleted: temp[index].deleted ? false : true,
                      };
                    else temp.splice(index, 1);
                    return temp;
                  })
                }
              />
              <OptionIcon
                as={Edit}
                onClick={() => {
                  setShowState({ index, state: "EDIT" });
                  setTitle(item.title);
                }}
              />

              {item.published && (
                <OptionIcon as={Search} onClick={() => onItemClick(item)} />
              )}
            </Options>
          </Row>
        )}
        onSortEnd={onSortEnd}
      />
    </Container>
  );
};

const TreeOptions = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: 10px;
`;

const ModalTitle = styled.div`
  font-size: 1.2em;
  font-weight: 600;
`;

const ModalStyled = styled(Modal)`
  max-width: 300px;
  width: 100%;
`;

const ModalContainer = styled.div`
  padding: 20px;

  & > *:not(:last-child) {
    margin-bottom: 10px;
  }
`;

const ButtonStyled = styled(Button)`
  margin: 15px 0px;
`;

const Buttons = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: space-between;
  width: 100%;

  @media (max-width: 240px) {
    flex-wrap: wrap;
    flex-direction: column-reverse;
    justify-content: center;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const Row = styled.div`
  ${(props) =>
    props.published
      ? css`
          background-color: #caedff;
        `
      : css`
          background-color: #eceff1;
        `}

  ${(props) =>
    props.deleted &&
    css`
      background-color: #f8bbd0;
    `}
  padding: 15px 15px;
  border-radius: 10px;
  overflow: hidden;
  box-sizing: border-box;
  width: 100%;
  user-select: none;
  &:not(:last-child) {
    margin-bottom: 10px;
  }
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: space-between;
`;

const Text = styled.div`
  font-size: 0.9em;
`;

const Options = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const OptionIcon = styled.div`
  width: 20px;
  height: 20px;
  min-width: 20px;
  min-height: 20px;
  padding: 10px;
  cursor: pointer;
  fill: rgba(0, 0, 0, 0.5);
`;

export default Tree;
